import { CaseClient, KitPopulationClient, KitPopulationFreqModel, KitPopulationSystemModel } from '@app/api2/api';
import SemanticField from '@app/components/SemanticField';
import { FieldArray } from 'formik';
import { customAlphabet, nanoid } from 'nanoid';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Button, Container, Divider, Form, FormGroup, Grid, GridColumn, GridRow, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from 'semantic-ui-react';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { AlleleConstants } from '@common/AlleleConstants';
import { BPForm } from '@common/BPForm';
import { PageHeader } from '@common/PageHeader';
import { insertComponentsIntoText } from '@common/Utils';
import { BPButton } from '@/components/bp/bpbutton';
import { BPContainer } from '@/components/bp/bpcontainer';


interface ICustomPanelProps {
    labId: number;
    caseId: number;
    panelId?: number;
}

interface Population {
    name: string;
    systems: KitPopulationSystemModelEx[];
}

type KitPopulationSystemModelEx = Omit<KitPopulationSystemModel, 'frequencies'> & {
    _id: string;
    frequencies: KitPopulationFreqModelEx[];
}

type KitPopulationFreqModelEx = KitPopulationFreqModel & {
    _id: string;
}

const nanoid1 = customAlphabet('1234567890', 5)
const genId = () => nanoid1()

const getBlankAlleleFreq = () => {
    let id = genId()
    // console.log(id)
    return {
        _id: genId(), allele: "", frequency: 0
    }
};

const blankInitValues = () => { return { name: "", systems: [...Array(1).keys()].map(x => { return { _id: genId(), name: "", frequencies: [...Array(1).keys()].map(f => { return getBlankAlleleFreq() }) } }) } as Population }

const client = new CaseClient()
const popClient = new KitPopulationClient()

export const CustomPanel: React.FunctionComponent<ICustomPanelProps> = (props) => {

    const [caseName, setCaseName] = React.useState('-')
    const [panelDetails, setPanelDetails] = React.useState<Population>(blankInitValues())

    React.useEffect(() => {
        async function fetch() {
            let res = await client.getCaseBasicInfo(props.labId, props.caseId);
            setCaseName(res.name)

            if (props.panelId) {
                let res2 = await popClient.get(props.panelId)
                setPanelDetails({ name: res2.population.name, systems: res2.population.systems.map(s => { return { _id: genId(), name: s.system, frequencies: s.alleleFrequencies?.filter(x => x.allele != AlleleConstants.RestAllele).map(a => { return { _id: genId(), allele: a.allele ?? '', frequency: a.frequency } }) } }) })
            }
        }

        fetch()
    }, [props.caseId])

    const submit = async (values: Population) => {
        if (props.panelId != null) {
            let result = await client.updateCustomPanel({ caseId: props.caseId, labId: props.labId, name: values.name, systems: values.systems, panelId: props.panelId }, props.labId.toString(), props.caseId.toString(), props.panelId.toString())
            if (result.success) {
                window.location.assign(buildUrl(AppRoutes.CaseDetails, { caseId: props.caseId, labId: props.labId }));
            } else {
                // console.log(result.error)
            }
        } else {
            let result = await client.createCustomPanel({ caseId: props.caseId, labId: props.labId, name: values.name, systems: values.systems }, props.labId.toString(), props.caseId.toString())
            if (result.success) {
                window.location.assign(buildUrl(AppRoutes.CaseDetails, { caseId: props.caseId, labId: props.labId }));
            } else {
                // console.log(result.error)
            }
        }
    };

    return <BPContainer>
        <PageHeader header={insertComponentsIntoText(props.panelId == null ? window.translation.customPanel.uploadTitle : window.translation.customPanel.editTitle, {
            '{caseName}': <Link key={'caseLink'} to={buildUrl(AppRoutes.CaseDetails, {
                caseId: props.caseId,
                labId: props.labId
            })}>{caseName}</Link>,
            '{panelName}': panelDetails.name
        })} />
        <Grid columns={1}>
            <GridRow>
                <GridColumn width={10}>
                    <BPForm initialValues={panelDetails} onSubmit={submit}>
                        {(formikProps) => {
                            return <>
                                <SemanticField
                                    label={window.translation.population.name}
                                    name={formikProps.getFieldName('name')}
                                    component={Form.Input}
                                />
                                <FieldArray name='systems'>
                                    {(faProps) => {
                                        return <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHeaderCell>{window.translation.common.system}</TableHeaderCell>
                                                    <TableHeaderCell>{window.translation.common.allele}</TableHeaderCell>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {formikProps.values.systems.map((s, idx) => {
                                                    return <TableRow key={`row_${s._id}`}>
                                                        <TableCell verticalAlign={'top'}>
                                                            <FormGroup inline>
                                                                <SemanticField
                                                                    name={`systems.${idx}.name`}
                                                                    placeholder={window.translation.common.system}
                                                                    component={Form.Input}
                                                                />
                                                                <BPButton disabled={formikProps.values.systems.length <= 1} type='button' icon='remove' size='small' onClick={() => { faProps.remove(idx) }}></BPButton>
                                                                <BPButton disabled={idx < 1 || idx > formikProps.values.systems.length - 1} type='button' icon='caret up' size='small' onClick={() => { faProps.move(idx, idx - 1) }}></BPButton>
                                                                <BPButton disabled={idx >= formikProps.values.systems.length - 1} type='button' icon='caret down' size='small' onClick={() => { faProps.move(idx, idx + 1) }}></BPButton>

                                                            </FormGroup>
                                                        </TableCell>
                                                        <TableCell verticalAlign={'top'}>
                                                            <FieldArray name={`systems.${idx}.frequencies`}>
                                                                {(frProps) => {
                                                                    return <>
                                                                        {s.frequencies.map((a, aidx) => {
                                                                            return <React.Fragment key={`af_${s._id}_${a._id}`}>
                                                                                <FormGroup>
                                                                                    <SemanticField
                                                                                        style={{ width: '70px' }}
                                                                                        name={`systems.${idx}.frequencies.${aidx}.allele`}
                                                                                        placeholder={window.translation.common.allele}
                                                                                        component={Form.Input}

                                                                                    />
                                                                                    <SemanticField
                                                                                        name={`systems.${idx}.frequencies.${aidx}.frequency`}
                                                                                        placeholder={window.translation.common.frequency}
                                                                                        component={Form.Input}
                                                                                        type='number'

                                                                                    />
                                                                                    <BPButton disabled={s.frequencies.length <= 1} type='button' icon='minus' size='small' onClick={() => { frProps.remove(aidx) }}></BPButton>
                                                                                    {/* {aidx == s.frequencies.length - 1 &&
                                                                        <BPButton type='button' icon='add' size='small' onClick={() => { frProps.insert(s.frequencies.length, { allele: '', frequency: null } as KitPopulationFreqModel) }}></BPButton>
                                                                    } */}
                                                                                </FormGroup>
                                                                                <Divider />
                                                                            </React.Fragment>
                                                                           })}
                                                                        <BPButton type='button' compact size='tiny' onClick={() => { frProps.insert(s.frequencies.length, getBlankAlleleFreq() as KitPopulationFreqModelEx) }}>Add Allele Frequency</BPButton>

                                                                    </>
                                                                }}
                                                            </FieldArray>
                                                        </TableCell>
                                                    </TableRow>
                                                })}
                                                <TableRow>
                                                    <TableCell>
                                                        <BPButton type='button' compact size='tiny' onClick={() => { faProps.insert(formikProps.values.systems.length, { _id: genId(), name: "", frequencies: [getBlankAlleleFreq()] } as KitPopulationSystemModelEx) }}>Add System</BPButton>
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    }}
                                </FieldArray>
                            </>
                        }}
                    </BPForm>
                </GridColumn>
            </GridRow>
        </Grid>
    </BPContainer>;
};


